import { default as LockIcon } from '@mui/icons-material/Lock'
import { default as CloseIcon } from '@mui/icons-material/Close'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { useIdleScreen } from 'src/customHooks/customHooks'
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import { IdlePopupaStyledDialog } from './components/styledComponents'
import { logScreenIdleClinicAssistant, checkOutClinicAssistant } from './clinicAssistantServices'

/**
 * Clinic Assistant Idle Popup Component
 * Similar to EmrCheckedInIdlePopup but for clinic assistants
 */
export function ClinicAssistantIdlePopup({
  currentClinicLocationData,
  clinicLocationId,
  assistantName = '',
  externalIsIdle = null, // Optional: external control of idle state
  onResetIdle = null, // Optional: callback when idle is reset
}) {
  const { assistantData } = useContext(ClinicDataContext)
  const navigate = useNavigate()

  let item = localStorage.getItem('checkedInDetails')
  let peekData = localStorage.getItem('peekedInRoomDetails')
  const parsedItem = useMemo(() => (item ? JSON.parse(item) : null), [item])
  const peekedRoomData = useMemo(() => (peekData ? JSON.parse(peekData) : null), [peekData])

  // Use external idle state if provided, otherwise use internal hook
  const internalIdleHook = useIdleScreen({ idleTime: 180000, manualReset: true }) // 3 minutes
  const isIdle = externalIsIdle !== null ? externalIsIdle : internalIdleHook.isIdle
  const resetIdle = externalIsIdle !== null && onResetIdle ? onResetIdle : internalIdleHook.resetIdle

  const hasCheckedInBefore = !!parsedItem
  const checkedInOrPeekedInChamberRoomId = parsedItem?.chamberRoomId || peekedRoomData?.id || 'reception'
  const checkedInOrPeekedInClinicLocationId =
    parsedItem?.clinicLocationId ||
    peekedRoomData?.clinicLocationId ||
    clinicLocationId ||
    currentClinicLocationData?.id ||
    null
  const currentRoomName = parsedItem?.roomName || peekedRoomData?.title

  function refreshLocalStorageCheckInVars() {
    item = localStorage.getItem('checkedInDetails')
    peekData = localStorage.getItem('peekedInRoomDetails')
  }

  async function logAndResetIdle() {
    await logScreenIdleClinicAssistant(
      checkedInOrPeekedInChamberRoomId,
      'User interaction - resetting idle state',
      checkedInOrPeekedInClinicLocationId
    )
    resetIdle()
  }

  async function handleLeave() {
    await logScreenIdleClinicAssistant(
      checkedInOrPeekedInChamberRoomId,
      'User chose to leave - checking out',
      checkedInOrPeekedInClinicLocationId
    )
    
    const response = await checkOutClinicAssistant(
      checkedInOrPeekedInChamberRoomId,
      'Leave from idle popup',
      checkedInOrPeekedInClinicLocationId
    )
    
    // Navigate to CA home regardless of checkout response
    navigate('/ca-home')
  }

  useEffect(() => {
    const handler = async (event) => {
      await sleep(500)
      refreshLocalStorageCheckInVars()
    }

    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handler)
    return () => {
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handler)
    }
  }, [])

  useEffect(() => {
    if (isIdle) {
      logScreenIdleClinicAssistant(
        checkedInOrPeekedInChamberRoomId,
        'Automatic idle detection',
        checkedInOrPeekedInClinicLocationId
      )
    }
  }, [isIdle, checkedInOrPeekedInChamberRoomId, checkedInOrPeekedInClinicLocationId])

  useEffect(() => {
    resetIdle()
  }, [parsedItem])

  // Don't show if not checked in
  if (!hasCheckedInBefore) {
    return null
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <IdlePopupaStyledDialog
      open={isIdle}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? '24px 24px 0 0' : '24px',
          maxHeight: isMobile ? '95vh' : '90vh',
          margin: isMobile ? 0 : '32px',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #6779e3 0%, #7450aa 100%)',
          color: 'white',
          padding: { xs: '18px 16px', sm: '22px 24px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <LockIcon sx={{ fontSize: { xs: 24, sm: 28 }, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '17px' },
                mb: 0.5,
                letterSpacing: '-0.01em',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Screen paused
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.95,
                fontSize: { xs: '12px', sm: '13px' },
                fontWeight: 400,
                letterSpacing: '0.01em',
              }}
            >
              {currentRoomName}
              {currentClinicLocationData?.name && ` • ${currentClinicLocationData.name}`}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={logAndResetIdle}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: '24px 16px', sm: '28px 24px' },
          paddingBottom: { xs: '24px', sm: '28px' },
          textAlign: 'center',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 2.5 },
          }}
        >
          <Box
            sx={{
              width: { xs: 64, sm: 72 },
              height: { xs: 64, sm: 72 },
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(103, 121, 227, 0.1) 0%, rgba(116, 80, 170, 0.1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon sx={{ fontSize: { xs: 32, sm: 36 }, color: '#6779e3' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '16px', sm: '17px' },
              color: '#1a1a1a',
              mb: 0.5,
            }}
          >
            Are you there?
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#6b7280',
              fontSize: { xs: '14px', sm: '15px' },
              maxWidth: '400px',
            }}
          >
            You seem to have gone idle for a while.
            {parsedItem?.createdTime && (
              <>
                <br />
                You had checked in at {dayjs.utc(parsedItem.createdTime).local().format('h:mm A')}
              </>
            )}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: { xs: '16px', sm: '24px' },
          justifyContent: 'center',
          gap: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          variant="contained"
          onClick={logAndResetIdle}
          sx={{
            background: 'linear-gradient(135deg, #6779e3 0%, #7450aa 100%)',
            color: 'white',
            fontWeight: 700,
            padding: { xs: '12px 32px', sm: '14px 40px' },
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: { xs: '15px', sm: '16px' },
            minWidth: { xs: '100%', sm: '160px' },
            boxShadow: '0 4px 16px rgba(103, 121, 227, 0.4), 0 2px 8px rgba(116, 80, 170, 0.3)',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6bd1 0%, #5a3d88 100%)',
              boxShadow: '0 6px 20px rgba(103, 121, 227, 0.5), 0 4px 12px rgba(116, 80, 170, 0.4)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          Continue
        </Button>
        <Button
          variant="outlined"
          onClick={handleLeave}
          sx={{
            borderColor: 'rgba(103, 121, 227, 0.3)',
            color: '#6779e3',
            fontWeight: 600,
            padding: { xs: '10px 24px', sm: '12px 32px' },
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: { xs: '14px', sm: '15px' },
            minWidth: { xs: '100%', sm: '140px' },
            '&:hover': {
              borderColor: 'rgba(103, 121, 227, 0.5)',
              backgroundColor: 'rgba(103, 121, 227, 0.05)',
            },
          }}
        >
          Leave
        </Button>
      </DialogActions>
    </IdlePopupaStyledDialog>
  )
}
